<template>
  <v-app>
    <header class="lb-header" :class="{ scrolled: scrolled }">
      <div class="lb-header__inner">
        <router-link to="/" class="lb-logo" aria-label="Zur Startseite">
          <span class="lb-logo__name">Kilian Link</span>
          <span class="lb-logo__divider">|</span>
          <span class="lb-logo__brand">LinkBeratung</span>
        </router-link>

        <button
          class="lb-burger"
          :class="{ open: menuOpen }"
          aria-label="Menü öffnen"
          @click="menuOpen = !menuOpen"
        >
          <span></span><span></span><span></span>
        </button>

        <!-- Kein Admin/Login mehr auf linkberatung — Beratungswünsche werden
             zentral im Admin auf waizmann089.de verwaltet. -->
        <nav class="lb-nav" :class="{ open: menuOpen }">
          <router-link to="/" @click="menuOpen = false">Startseite</router-link>
          <router-link to="/konzept" @click="menuOpen = false">Konzept</router-link>
          <router-link to="/sprechstunde" @click="menuOpen = false" class="lb-nav__cta">
            Termin sichern
          </router-link>
        </nav>
      </div>
    </header>

    <main class="lb-main">
      <router-view v-slot="{ Component, route }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </main>

    <footer class="lb-footer">
      <div class="lb-footer__inner">
        <div class="lb-footer__brand">
          <div class="lb-logo">
            <span class="lb-logo__name">Kilian Link</span>
            <span class="lb-logo__divider">|</span>
            <span class="lb-logo__brand">LinkBeratung</span>
          </div>
          <p class="lb-footer__tag">
            Persönliche Telefonberatung für Zahnzusatzversicherungen.
            <br />
            Eine Entscheidung statt einer Auswahl.
          </p>
        </div>

        <nav class="lb-footer__nav">
          <h4>Navigation</h4>
          <router-link to="/">Startseite</router-link>
          <router-link to="/konzept">Konzept &amp; Über mich</router-link>
          <router-link to="/sprechstunde">Termin buchen</router-link>
          <router-link to="/impressum">Impressum</router-link>
        </nav>

        <div class="lb-footer__contact">
          <h4>Sprechstunde</h4>
          <p>Kostenlose Telefonberatung</p>
          <p>Dauer: ca. 10–15 Minuten</p>
          <router-link to="/sprechstunde" class="lb-footer__cta">
            Jetzt Termin sichern →
          </router-link>
        </div>
      </div>
      <div class="lb-footer__bottom">
        <span>&copy; {{ year }} LinkBeratung &middot; Kilian Link</span>
        <router-link to="/impressum">Impressum</router-link>
      </div>
    </footer>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";

const menuOpen = ref(false);
const scrolled = ref(false);
const route = useRoute();
const year = computed(() => new Date().getFullYear());

function onScroll() {
  scrolled.value = window.scrollY > 40;
}

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
});

import { watch } from "vue";
watch(
  () => route.fullPath,
  () => {
    menuOpen.value = false;
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }
);
</script>

<style>
:root {
  --lb-ink: #0b1f3b;
  --lb-ink-soft: #3a4a66;
  --lb-mute: #7b8aa1;
  --lb-cream: #fbf7f0;
  --lb-line: #e8e0d2;
  --lb-accent: #6c4cf1;
  --lb-cta: #f59e0b;
  --lb-cta-dark: #d97706;
}

html,
body {
  margin: 0;
  padding: 0;
  background: #fff;
  color: var(--lb-ink);
  font-family: "Lato", "Helvetica Neue", Arial, sans-serif;
  font-size: 18px;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

* {
  box-sizing: border-box;
}

a {
  color: var(--lb-accent);
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

h1,
h2,
h3,
h4 {
  font-family: "Playfair Display", "Georgia", serif;
  color: var(--lb-ink);
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 16px;
}

h1 {
  font-size: clamp(38px, 6vw, 72px);
  line-height: 1.05;
  font-weight: 800;
}

h2 {
  font-size: clamp(30px, 4.5vw, 52px);
  line-height: 1.1;
}

h3 {
  font-size: clamp(22px, 2.5vw, 30px);
  line-height: 1.25;
}

p {
  margin: 0 0 16px;
  color: var(--lb-ink-soft);
}

.lb-handwritten {
  font-family: "Caveat", cursive;
  color: var(--lb-cta);
  font-weight: 700;
}

.lb-container {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 28px;
}

.lb-container--narrow {
  max-width: 820px;
}

.lb-section {
  padding: 96px 0;
}

.lb-section--cream {
  background: var(--lb-cream);
}

.lb-section--dark {
  background: var(--lb-ink);
  color: #fff;
}

.lb-section--dark h1,
.lb-section--dark h2,
.lb-section--dark h3,
.lb-section--dark h4,
.lb-section--dark p {
  color: #fff;
}

@media (max-width: 768px) {
  .lb-section {
    padding: 64px 0;
  }
}

/* ---------- Header ---------- */
.lb-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: saturate(160%) blur(10px);
  border-bottom: 1px solid transparent;
  transition: all 0.25s ease;
}

.lb-header.scrolled {
  border-bottom-color: var(--lb-line);
  box-shadow: 0 6px 20px rgba(11, 31, 59, 0.04);
}

.lb-header__inner {
  max-width: 1240px;
  margin: 0 auto;
  padding: 18px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.lb-logo {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  text-decoration: none;
  color: var(--lb-ink);
  font-family: "Playfair Display", serif;
  font-size: 22px;
  font-weight: 700;
}

.lb-logo:hover {
  text-decoration: none;
}

.lb-logo__name {
  font-weight: 800;
}

.lb-logo__divider {
  color: var(--lb-cta);
  font-weight: 500;
}

.lb-logo__brand {
  color: var(--lb-accent);
  font-weight: 600;
  font-style: italic;
}

.lb-nav {
  display: flex;
  align-items: center;
  gap: 32px;
}

.lb-nav a {
  font-family: "Lato", sans-serif;
  color: var(--lb-ink);
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  text-decoration: none;
  position: relative;
}

.lb-nav a::after {
  content: "";
  position: absolute;
  bottom: -6px;
  left: 0;
  height: 2px;
  width: 0;
  background: var(--lb-cta);
  transition: width 0.25s ease;
}

.lb-nav a:hover::after,
.lb-nav a.router-link-active::after {
  width: 100%;
}

.lb-nav a.lb-nav__cta {
  background: var(--lb-cta);
  color: #fff;
  padding: 12px 22px;
  border-radius: 999px;
  box-shadow: 0 6px 18px rgba(245, 158, 11, 0.32);
}

.lb-nav a.lb-nav__cta:hover {
  background: var(--lb-cta-dark);
}

.lb-nav a.lb-nav__cta::after {
  display: none;
}

.lb-nav__login,
.lb-nav a.lb-nav__login,
.lb-nav__admin,
.lb-nav a.lb-nav__admin {
  font-family: "Lato", sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid var(--lb-line);
  background: #fff;
  color: var(--lb-ink);
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.lb-nav__login:hover,
.lb-nav a.lb-nav__login:hover,
.lb-nav__admin:hover,
.lb-nav a.lb-nav__admin:hover {
  border-color: var(--lb-accent);
  color: var(--lb-accent);
  text-decoration: none;
}

.lb-nav a.lb-nav__admin {
  border-color: var(--lb-accent);
  color: var(--lb-accent);
}
.lb-nav a.lb-nav__admin:hover {
  background: var(--lb-accent);
  color: #fff;
}

.lb-nav a.lb-nav__login::after,
.lb-nav a.lb-nav__admin::after {
  display: none;
}

.lb-burger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: transparent;
  border: 0;
  padding: 8px;
  cursor: pointer;
  z-index: 60;
}

.lb-burger span {
  width: 26px;
  height: 2.5px;
  background: var(--lb-ink);
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.2s ease;
}

.lb-burger.open span:nth-child(1) {
  transform: translateY(7.5px) rotate(45deg);
}

.lb-burger.open span:nth-child(2) {
  opacity: 0;
}

.lb-burger.open span:nth-child(3) {
  transform: translateY(-7.5px) rotate(-45deg);
}

@media (max-width: 860px) {
  .lb-header__inner {
    padding: 14px 22px;
  }
  .lb-burger {
    display: flex;
  }
  .lb-nav {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: min(360px, 85vw);
    background: #fff;
    padding: 100px 32px 32px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    box-shadow: -16px 0 40px rgba(11, 31, 59, 0.1);
  }
  .lb-nav.open {
    transform: translateX(0);
  }
  .lb-nav a {
    font-size: 18px;
  }
  .lb-logo {
    font-size: 18px;
  }
}

/* ---------- Main ---------- */
.lb-main {
  padding-top: 78px;
  min-height: 60vh;
}

/* ---------- Footer ---------- */
.lb-footer {
  background: var(--lb-ink);
  color: #cbd5e1;
  padding: 64px 0 28px;
  margin-top: 80px;
}

.lb-footer__inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 28px;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 48px;
}

.lb-footer .lb-logo,
.lb-footer .lb-logo__name,
.lb-footer .lb-logo__brand {
  color: #fff;
}

.lb-footer__tag {
  margin-top: 16px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.6;
}

.lb-footer h4 {
  color: #fff;
  font-family: "Playfair Display", serif;
  font-size: 20px;
  margin-bottom: 16px;
}

.lb-footer__nav,
.lb-footer__contact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lb-footer__nav a {
  color: #cbd5e1;
  font-weight: 500;
  font-size: 15px;
}

.lb-footer__nav a:hover {
  color: var(--lb-cta);
  text-decoration: none;
}

.lb-footer__contact p {
  color: #94a3b8;
  margin: 0;
  font-size: 15px;
}

.lb-footer__cta {
  margin-top: 12px;
  color: var(--lb-cta);
  font-weight: 700;
}

.lb-footer__bottom {
  max-width: 1140px;
  margin: 48px auto 0;
  padding: 24px 28px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #94a3b8;
  font-size: 14px;
}

.lb-footer__bottom a {
  color: #cbd5e1;
}

@media (max-width: 760px) {
  .lb-footer__inner {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .lb-footer__bottom {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
}

/* ---------- Page transition ---------- */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ---------- Vuetify overrides for typography fit ---------- */
.v-application {
  font-family: "Lato", sans-serif !important;
}
</style>
